import {
  validateSync,
  registerDecorator,
  ValidatorConstraint,
  type ValidationOptions,
  type ValidationArguments,
  type ValidatorConstraintInterface,
} from 'class-validator';
import { plainToInstance } from 'class-transformer';
import { RgbaDto } from '../RgbaDto';

export function validateRgba(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const rgbaInstance = plainToInstance(RgbaDto, value, {
    enableImplicitConversion: true,
  });

  const rgbaErrors = validateSync(rgbaInstance, {
    skipMissingProperties: false,
    whitelist: false,
    forbidNonWhitelisted: false,
  });

  return rgbaErrors.length === 0;
}

/**
 * Validates that a value is either:
 * 1. An RGBA object: { r: 0-255, g: 0-255, b: 0-255, a: 0-1 }
 * 2. A color reference string: '$ref:#/colors/color[1-6]'
 */
export
@ValidatorConstraint({ name: 'isRgbaOrReference', async: false })
class IsRgbaOrReferenceConstraint implements ValidatorConstraintInterface {
  validate(value: unknown) {
    if (value === null) {
      return true;
    }

    if (typeof value === 'string') {
      const colorRefPattern = /^\$ref:#\/colors\/color[1-6]$/;
      return colorRefPattern.test(value);
    }

    return validateRgba(value);
  }

  defaultMessage(args: ValidationArguments) {
    return `${args.property} must be either a valid RGBA object {r: 0-255, g: 0-255, b: 0-255, a: 0-1} or a reference string like '$ref:#/colors/color1'`;
  }
}

export function IsRgbaOrReference(validationOptions?: ValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      constraints: [],
      validator: IsRgbaOrReferenceConstraint,
    });
  };
}
